import React, { FC, ReactNode } from 'react';

export type TileTone = 'primary' | 'emerald' | 'amber' | 'indigo' | 'blue' | 'red' | 'slate';

/** Gradient + icon colour per tone, matching the existing Quick Action cards. */
const TONE: Record<TileTone, { from: string; to: string; icon: string; hoverBorder: string; hoverShadow: string }> = {
  primary: { from: 'rgba(30, 95, 138, 0.14)', to: 'rgba(30, 95, 138, 0.06)', icon: 'text-brand-primary', hoverBorder: 'hover:border-brand-primary/40', hoverShadow: 'hover:shadow-[0_8px_30px_-12px_rgba(30,95,138,0.25)]' },
  emerald: { from: 'rgba(45, 138, 95, 0.14)', to: 'rgba(45, 138, 95, 0.06)', icon: 'text-brand-success', hoverBorder: 'hover:border-emerald-300', hoverShadow: 'hover:shadow-[0_8px_30px_-12px_rgba(45,138,95,0.25)]' },
  amber: { from: 'rgba(217, 119, 6, 0.14)', to: 'rgba(217, 119, 6, 0.06)', icon: 'text-amber-600', hoverBorder: 'hover:border-amber-300', hoverShadow: 'hover:shadow-[0_8px_30px_-12px_rgba(217,119,6,0.25)]' },
  indigo: { from: 'rgba(79, 70, 229, 0.14)', to: 'rgba(79, 70, 229, 0.06)', icon: 'text-indigo-600', hoverBorder: 'hover:border-indigo-300', hoverShadow: 'hover:shadow-[0_8px_30px_-12px_rgba(79,70,229,0.25)]' },
  blue: { from: 'rgba(37, 99, 235, 0.14)', to: 'rgba(37, 99, 235, 0.06)', icon: 'text-blue-600', hoverBorder: 'hover:border-blue-300', hoverShadow: 'hover:shadow-[0_8px_30px_-12px_rgba(37,99,235,0.25)]' },
  red: { from: 'rgba(220, 38, 38, 0.12)', to: 'rgba(220, 38, 38, 0.06)', icon: 'text-red-600', hoverBorder: 'hover:border-red-300', hoverShadow: 'hover:shadow-[0_8px_30px_-12px_rgba(220,38,38,0.25)]' },
  slate: { from: 'rgba(100, 116, 139, 0.14)', to: 'rgba(100, 116, 139, 0.06)', icon: 'text-slate-600', hoverBorder: 'hover:border-slate-300', hoverShadow: 'hover:shadow-[0_8px_30px_-12px_rgba(100,116,139,0.25)]' },
};

interface ActionTileProps {
  icon: string;
  tone: TileTone;
  title: string;
  /**
   * D12 — live state shown BESIDE the title, at the same size, rather than in
   * the smaller subtitle line. Rendered as "Title — STATE". The pair is a
   * flex-wrap row, so on a narrow phone the state drops to a second line at the
   * SAME size instead of shrinking or being truncated.
   */
  titleState?: string;
  /** Optional — a tile with no approved subtitle string renders title-only. */
  subtitle?: ReactNode;
  onClick: () => void;
  /**
   * Live on/off state. `undefined` = not a stateful tile, or state not loaded
   * yet — no dot is drawn rather than asserting "off" about something unread.
   */
  state?: boolean;
}

/**
 * The large dashboard tile used by the Quick Actions grid.
 *
 * Extracted during the 2026-08-10 UX wave: that wave added five more tiles and
 * repeating this markup inline seven times made the dashboard unreadable. The
 * markup is a faithful copy of the pre-existing cards (same padding, radius,
 * icon plate, hover lift, arrow reveal), so new and old tiles are the same
 * family by construction rather than by careful copying.
 */
export const ActionTile: FC<ActionTileProps> = ({ icon, tone, title, titleState, subtitle, onClick, state }) => {
  const c = TONE[tone];
  return (
    <button
      onClick={onClick}
      className={`group relative flex items-center gap-4 p-5 bg-white border border-brand-border/70 rounded-2xl text-left overflow-hidden transition-all duration-300 hover:-translate-y-0.5 ${c.hoverBorder} ${c.hoverShadow}`}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
        style={{ background: `linear-gradient(135deg, ${c.from} 0%, ${c.to} 100%)` }}
      >
        <span
          className={`material-symbols-outlined ${c.icon}`}
          style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}
        >
          {icon}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base font-semibold text-brand-text leading-snug flex flex-wrap items-baseline gap-x-1.5">
          <span>{title}</span>
          {titleState && (
            /* Same size as the title on purpose — this is state the caregiver
               reads at a glance, not a caption. flex-wrap lets it fall to a
               second line on a narrow phone rather than truncating. */
            <span className="text-brand-text-muted whitespace-nowrap">— {titleState}</span>
          )}
        </div>
        {/* D15 — subtitle is 14px, not 12px. The persona is a 45-65yo caregiver
            reading her phone at 11pm; 12px muted grey is the first thing that
            stops being legible. 14px is the body floor. */}
        {(subtitle !== undefined || state !== undefined) && (
        <div className="text-sm text-brand-text-muted mt-0.5 flex items-center gap-1.5">
          {state !== undefined && (
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                state ? 'bg-emerald-500' : 'bg-brand-text-muted/40'
              }`}
              aria-hidden="true"
            />
          )}
          {subtitle !== undefined && <span className="truncate">{subtitle}</span>}
        </div>
        )}
      </div>
      <span
        className="material-symbols-outlined text-brand-text-muted/50 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
        style={{ fontSize: '18px' }}
      >
        arrow_forward
      </span>
    </button>
  );
};

export default ActionTile;
